// Global OAuth callback redirect — handles the stable /oauth/mcp/callback path
// and forwards to the branch-prefixed settings callback route.
// The redirect URI registered with OAuth providers is always /oauth/mcp/callback
// regardless of branch deployment, and this redirect forwards to the real handler.
// This must run before any other imports to avoid unnecessary work.
if (window.location.pathname === "/oauth/mcp/callback") {
  const branchPrefix = import.meta.env.VITE_BRANCH_NAME
    ? `/${import.meta.env.VITE_BRANCH_NAME}`
    : "";
  window.location.replace(
    `${branchPrefix}/settings/ai/mcp-callback${window.location.search}`,
  );
}

// Credential request approval links from the CLI's browser sign-in also point at the stack root;
// on a dev stack, forward them under the branch prefix the same way.
{
  const branchLocation = branchPrefixedLocation(
    window.location.pathname,
    window.location.search,
    import.meta.env.VITE_BRANCH_NAME as string | undefined,
  );
  if (branchLocation !== undefined) {
    window.location.replace(branchLocation);
  }
}

// This module must be imported before any other module that defines Zod schemas.
// oxlint-disable-next-line import-x-js/order
import "./zodConfig";

// Starts Sentry before the rest of the app's modules evaluate, so a module that
// throws while loading is still reported.
// oxlint-disable-next-line import-x-js/order
import "./initErrorMonitoring";

import "@fontsource/roboto/100.css";
import "@fontsource/roboto/200.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/600.css";
import "@fontsource/roboto/700.css";
import "@fontsource/roboto/800.css";
import "@fontsource/roboto/900.css";
import "@fontsource/roboto-mono/400.css";
import "@fontsource/roboto-mono/500.css";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "@/App";
import { branchPrefixedLocation } from "@/App/branchStablePaths";
import "@/App/styles/globals.css";
import "@/App/styles/theme.css";
import { configureAmplify } from "@/shared/auth";
import { registerBoardAuthoringService } from "@/shared/domain/boards/boardAuthoringService";
import "@/shared/foundation/styles/colors.css";
import "@/shared/foundation/styles/spacing.css";
import "@/shared/foundation/styles/typography.css";
import "@/shared/foundation/styles/utilities.css";

import { configureMonaco } from "./App/configureMonaco";

await configureAmplify();

// Setup Monaco code editor but doesn't load it until it's needed
configureMonaco();

// The chat's board tools and their confirmation cards reach the Boards feature
// through this loader, so the Boards chunk loads when one of them first needs it.
registerBoardAuthoringService(() =>
  import("@/Boards/chatAuthoring/authoringService").then(
    (m) => m.boardAuthoringService,
  ),
);

dayjs.extend(duration);
dayjs.extend(relativeTime);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
